import { createModalStore } from '@bridge/ui';
import { z } from 'zod';

import env from 'lib/env';

import type { BaseModalProps, ModalProps as FactoryModalProps } from '@bridge/ui';

// Modal registry with dynamic imports
const modalRegistry = {
  PaymentRouteInstructionModal: () =>
    import('../app/app/payments/_components/CreateRoute/PaymentRouteInstructionModal'),
  // CreateCustomer's only deep link carries no payload, so its URL schema accepts an empty object only.
  CreateCustomer: {
    component: () => import('../app/app/customers/_components/CreateCustomerModal'),
    urlOpenable: z.strictObject({}),
  },
  AddMember: () => import('../app/app/settings/organization/team/_components/AddMemberModal'),
  PendingApproval: () => import('../app/app/settings/organization/team/_components/PendingApprovalModal'),
  AddWallet: () => import('../app/app/wallets/developer_wallets/_components/AddWalletModal'),
  ChangeRoleModal: () => import('../app/app/settings/organization/team/_components/ChangeRoleModal'),
  ConfirmModal: () => import('@bridge/ui').then((m) => ({ default: m.ConfirmationModal })),
  DeleteModal: () => import('../components/modal/DeleteModal'),
  DeveloperSupportInfoModal: () => import('../components/modal/DeveloperSupportInfoModal'),
  CryptoReturnPolicyModal: () =>
    import('../app/app/settings/product/crypto-return-policy/_components/CryptoReturnPolicyModal'),
  SwitchReturnToSenderPolicyModal: () =>
    import('../app/app/settings/product/crypto-return-policy/_components/SwitchReturnToSenderPolicyModal'),
  SwitchStaticAddressPolicyModal: () =>
    import('../app/app/settings/product/crypto-return-policy/_components/SwitchStaticAddressPolicyModal'),
  SaveApiKeyModal: () => import('../app/app/keys/_components/SaveApiKeyModal'),
  OnboardingSurveyModal: () => import('../app/app/home/_components/OnboardingSurveyModal'),
  OnboardingChecklistDismissModal: () => import('../app/app/home/_components/OnboardingChecklistDismissModal'),
  ViewWebhookEventModal: () => import('../app/app/home/_components/tasks/ViewWebhookEventModal'),
  FreezeCardModal: () => import('../app/app/cards/_components/FreezeCardModal'),
  UpdateDeveloperFeeModal: () => import('../app/app/payments/_components/UpdateDeveloperFeeModal'),
  UpdatePaymentRouteDestinationModal: () =>
    import('../app/app/payments/_components/UpdatePaymentRouteDestinationModal'),
  CreateRole: () => import('../app/app/settings/organization/roles/_components/CreateRoleModal'),
  RenameRoleModal: () => import('../app/app/settings/organization/roles/_components/RenameRoleModal'),
  TwoFactorSetupModal: () => import('../components/modal/TwoFactorSetupModal'),
  TwoFactorVerifyModal: () => import('../components/modal/TwoFactorVerifyModal'),
  RequestFeatureAccess: () =>
    import('../app/app/customers/[id]/_components/customer-details/compliance/RequestFeatureAccessModal'),
  DeveloperRejectionReason: () =>
    import('../app/app/customers/[id]/_components/customer-details/compliance/DeveloperRejectionReasonModal'),
  EditCustomerDetail: () =>
    import('../app/app/customers/[id]/_components/customer-details/compliance/EditCustomerDetailModal'),
  AddExternalAccount: () =>
    import('../app/app/customers/[id]/_components/customer-details/external-accounts/AddExternalAccountFormModal'),
  EditExternalAccount: () =>
    import('../app/app/customers/[id]/_components/customer-details/external-accounts/EditExternalAccountModal'),
  DeleteExternalAccount: () =>
    import('../app/app/customers/[id]/_components/customer-details/external-accounts/DeleteExternalAccountModal'),
  WebhookModal: () => import('../app/app/settings/product/webhooks/_components/WebhookModal'),
  EditThresholdModal: () => import('../app/app/settings/organization/fixed-trade-rates/_components/EditThresholdModal'),
  EditApprovalRuleModal: () => import('../app/app/settings/organization/approvals/_components/EditApprovalRuleModal'),
  IssuanceRewardsWallet: () =>
    import('../app/app/settings/organization/issuance-rewards/_components/IssuanceRewardsWalletModal'),
} as const;

export type ModalIdentifier = keyof typeof modalRegistry;

export type ModalProps<ID extends ModalIdentifier> = FactoryModalProps<typeof modalRegistry, ID>;

export type { BaseModalProps };

export const {
  useModalStore,
  useModal,
  preloadModals,
  ModalRenderer: BaseModalRenderer,
  getModalUrl,
} = createModalStore<typeof modalRegistry>(modalRegistry, {
  baseUrl: env.DOMAIN,
});

export { modalRegistry };
